import React, { useState } from 'react';
import './ProjectGroupHeader.scss';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { useTranslation } from 'react-i18next';
import { ProjectGroup } from '../../../../shared/models/projects/ProjectGroup';
import { useUpdateProjectGroup, useDeleteProjectGroup } from '../../../../shared/hooks/projects/UseProjectGroups';
import { showCustomDialog } from '../../../../shared/helpers/DialogHelper';
import CustomTooltip from '../../../../shared/components/common/tooltip/CustomTooltip';
import { Tooltip } from 'primereact/tooltip';

interface ProjectGroupHeaderProps {
  group: ProjectGroup;
  projectCount: number;
  isCollapsed: boolean;
  onToggleCollapse: () => void;
  onDeleteSuccess?: () => void;
}

const ProjectGroupHeader: React.FC<ProjectGroupHeaderProps> = ({
  group,
  projectCount,
  isCollapsed,
  onToggleCollapse,
  onDeleteSuccess,
}) => {
  const { t } = useTranslation();
  const [isEditing, setIsEditing] = useState(false);
  const [editName, setEditName] = useState(group.name);

  const { mutate: updateGroup } = useUpdateProjectGroup();
  const { mutate: removeGroup } = useDeleteProjectGroup(onDeleteSuccess);

  const handleSaveName = () => {
    const trimmed = editName.trim();
    if (trimmed && trimmed !== group.name) {
      updateGroup({ groupId: group.id, data: { name: trimmed } });
    }
    setIsEditing(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') handleSaveName();
    if (e.key === 'Escape') {
      setEditName(group.name);
      setIsEditing(false);
    }
  };

  const confirmDelete = () => {
    showCustomDialog({
      accept: () => removeGroup(group.id),
    });
  };

  return (
    <div className={`project-group-header${isCollapsed ? ' project-group-header--collapsed' : ''}`} onClick={onToggleCollapse}>
      <div className="project-group-header__left">
        <span className="ico--up ico--xs project-group-header__chevron" />
        <span
          className="project-group-header__dot"
          style={{ backgroundColor: group.color || 'var(--grey-ux-400)' }}
        />
        {isEditing ? (
          <InputText
            value={editName}
            onChange={(e) => setEditName(e.target.value)}
            onBlur={handleSaveName}
            onKeyDown={handleKeyDown}
            autoFocus
            className="project-group-header__input"
            onClick={(e) => e.stopPropagation()}
          />
        ) : (
          <>
            <span id={`group-name-${group.id}`} className="project-group-header__name">{group.name}</span>
            <Tooltip target={`#group-name-${group.id}`} content={group.name} position="top" showDelay={300} />
          </>
        )}
        <span className="project-group-header__count">{projectCount}</span>
      </div>
      <div className="project-group-header__actions">
        <Button
          id={`btn-edit-group-${group.id}`}
          className="action-btn"
          onClick={(e) => {
            e.stopPropagation();
            setIsEditing(true);
          }}
        >
          <span className="ico--edit ico--xs" />
        </Button>
        <CustomTooltip tooltipText="editGroup" targetRef={`btn-edit-group-${group.id}`} />

        <Button
          id={`btn-remove-group-${group.id}`}
          className="action-btn"
          onClick={(e) => {
            e.stopPropagation();
            confirmDelete();
          }}
        >
          <span className="ico--trash ico--xs" />
        </Button>
        <CustomTooltip tooltipText="removeGroup" targetRef={`btn-remove-group-${group.id}`} />
      </div>
    </div>
  );
};

export default ProjectGroupHeader;
